import { z } from 'zod';
import { LocalizedStringSchema } from './car';
import { DamageSeverity, DataSource } from './car-enums';
import { DealScoreSchema } from './deal-score';
/**
 * Sales-archive contract (phase 12) — the compact snapshot persisted to the
 * `cars_archive` collection when the vehicle-cleanup cron removes an ended lot
 * from `alpicar_cars` (or when the backfill script archives ended lots).
 *
 * Deliberately COMPACT: the archive powers the public "sold cars" pages and
 * the price-history stats endpoint, not a full lot restore. Everything heavy
 * (image galleries, raw_data, equipment lists) is dropped; `first_image` is
 * the single S3 URL that survives (S3 objects themselves are deleted by
 * cleanup — the URL is retained for lots archived by backfill while their
 * images still exist).
 *
 * Field mapping (source of truth: archived-car.mapper.ts in apps/api):
 *  - final_bid: numeric — current_bid_value when present, else the parsed
 *    `current_bid` string (Swiss formats like "12'500" / "CHF 12 500" parse).
 *  - sold_at: the lot's auction_end_date (BSON Date).
 *  - damage_severity comes from ai_car_analysis; damage_description from the
 *    lot (multilingual) — together the "damage summary".
 *  - deal_score: the persisted ai_car_analysis.deal_score snapshot (phase 10).
 */
export const ArchivedCarSchema = z
    .object({
    // ── Identity ─────────────────────────────────────────────────────────
    listing_id: z.string(),
    data_source: z.nativeEnum(DataSource).default(DataSource.ALPICAR),
    // ── Vehicle summary ──────────────────────────────────────────────────
    brand: z.string().nullish(),
    model: z.string().nullish(),
    year: z.number().int().nullish(),
    fuel_type: LocalizedStringSchema.nullish(), // ML
    mileage_km: z.number().int().nullish(),
    // ── Damage summary ───────────────────────────────────────────────────
    damage_severity: z.nativeEnum(DamageSeverity).nullish(),
    damage_description: LocalizedStringSchema.nullish(), // ML
    // ── Sale outcome ─────────────────────────────────────────────────────
    /** Last known bid at auction end, numeric CHF (see mapper note above). */
    final_bid: z.number().nullish(),
    first_image: z.string().nullish(),
    sold_at: z.coerce.date().nullish(),
    // ── Analysis snapshot ────────────────────────────────────────────────
    deal_score: DealScoreSchema.nullish(),
    // ── Bookkeeping ──────────────────────────────────────────────────────
    archived_at: z.coerce.date().nullish(),
})
    // cars_archive is strict:false like every shared collection — unmodeled
    // fields (Mongo _id, future additions) must survive round-trips.
    .passthrough();
/** Total number of modeled fields — frozen by contract tests. */
export const ARCHIVED_CAR_SCHEMA_FIELD_COUNT = Object.keys(ArchivedCarSchema.shape).length;
//# sourceMappingURL=archive.js.map